import { buildFileUploadContext } from '@common/Copilot/CopilotAgentChat/FileUpload/util';
import { useProductDataboxSource } from '@common/Databox/productDataSource';
import { useDataboxMultiUpload } from '@common/Databox/useDataboxMultiUpload';
import { useOrchestrationAuth } from '@common/Fabric/OrchestrationFabric/OrchestrationAuth';
import { useFileUploadSizeGate } from '@common/FileUpload/useFileUploadSizeGate';
import { writePath } from '@prophecy/shared-core/databox/api';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { HOME_CHAT_QUERY_KEY, HOME_CHAT_UPLOAD_FOLDER } from './constants';

import type { DataboxUploadItem } from '@prophecy/shared-core/databox/adapter';
import type { DataboxHome } from '@prophecy/shared-core/databox/port';

/**
 * Composer attachments: uploaded to the user's own Databox folder (the Databox Home path the
 * backend returns) as soon as they are attached, then sent as `@file:` context — the paths the
 * sandbox tells the agent to read with the Databox tools.
 */
export function useHomeChatAttachments() {
  const source = useProductDataboxSource();
  const upload = useDataboxMultiUpload();
  const { gate } = useFileUploadSizeGate();
  const [attached, setAttached] = useState<File[]>([]);
  // Files already in user-uploads that a pasted prompt names; nothing to upload, only to send.
  const [restored, setRestored] = useState<DataboxUploadItem[]>([]);

  // Read Home only once the fabric's token has settled, as the Databox page does: earlier, the
  // read goes out with an empty token and nothing asks again.
  const fabricId = useOrchestrationAuth((state) => state.fabricId);
  const tokenSettled = useOrchestrationAuth(({ jwt, error }) => Boolean(jwt || error));
  const home = useQuery([HOME_CHAT_QUERY_KEY, 'databoxHome', fabricId], () => source.summary(), {
    enabled: Boolean(fabricId && tokenSettled)
  });

  const items = useMemo(
    () => [...upload.items.filter((item) => attached.includes(item.file)), ...restored],
    [upload.items, attached, restored]
  );
  const isUploading = items.some((item) => item.status === 'pending' || item.status === 'uploading');

  // Created once per Home path, not once per mount: switching fabric gives a different Home, and a
  // cached promise from the old one would leave the folder missing under the new. A repeat call for
  // a folder already there rejects, and that is not a failure — the upload itself proves it usable.
  const ensured = useRef<{ key: string; done: Promise<void> }>();
  const ensureStagingFolder = usePersistentCallback((data: DataboxHome) => {
    const key = data.path.join('/');
    if (ensured.current?.key !== key) {
      const done = Promise.resolve(source.createFolder?.(data.path, HOME_CHAT_UPLOAD_FOLDER)).catch(() => undefined);
      ensured.current = { key, done };
    }
    return ensured.current.done;
  });

  const add = usePersistentCallback(async (files: File[]) => {
    if (!home.data) return;
    const { allowedFiles } = await gate(files, [], { trackStart: false });
    if (!allowedFiles.length) return;
    await ensureStagingFolder(home.data);
    setAttached((prev) => [...prev, ...allowedFiles]);
    upload.enqueue(allowedFiles, writePath([...home.data.path, HOME_CHAT_UPLOAD_FOLDER]));
  });

  const restore = usePersistentCallback((names: string[]) => {
    if (!home.data) return;
    const folder = [...home.data.path, HOME_CHAT_UPLOAD_FOLDER];
    setRestored((prev) => {
      const seen = new Set(items.map((item) => item.file.name));
      const next = [...prev];
      for (const name of names) {
        if (seen.has(name)) continue;
        seen.add(name);
        next.push({
          id: `restored-${name}`,
          file: new File([], name),
          status: 'done',
          path: writePath([...folder, name]),
          error: null,
          progress: 100
        });
      }
      return next;
    });
  });

  const remove = usePersistentCallback((itemId: string) => {
    if (restored.some((item) => item.id === itemId)) {
      setRestored((prev) => prev.filter((item) => item.id !== itemId));
      return;
    }
    const item = items.find((candidate) => candidate.id === itemId);
    if (!item) return;
    if (item.status === 'done') upload.removeItem(item.id);
    else upload.cancelItem(item.id);
    setAttached((prev) => prev.filter((file) => file !== item.file));
  });

  const clear = usePersistentCallback(() => {
    items.filter((item) => !restored.includes(item)).forEach((item) => upload.removeItem(item.id));
    setAttached([]);
    setRestored([]);
  });

  const context = usePersistentCallback(() => {
    const done = items.filter((item) => item.status === 'done' && item.path);
    if (!done.length) return undefined;
    return buildFileUploadContext(
      done.map((item) => item.file),
      new Map(done.map((item) => [item.file, item.path as string]))
    );
  });

  return {
    items,
    isReady: Boolean(home.data),
    isUnavailable: home.isError,
    isUploading,
    add,
    restore,
    remove,
    clear,
    context
  };
}
